<script>
  import { afterUpdate, onMount } from "svelte"
  import { releaseSelectedDocument } from "../../../Script/bulkUpload"
  import notify from "../../../../../../Script/notify"
  import bulkJson from "../../../Stores/jsonBulkData"
  import { createEventDispatcher } from "svelte"
  import Loader from "../../../../../../Shared/Slots/loader.svelte"
  import { loadTooltip } from "../../../../../../Script/loadTooltip"
  import axios from "axios"

  const dispatch = createEventDispatcher()

  /**
   * @type {import("../../../Script/document".bulkDocument)}
   */
  export let documentData
  /**
   * @type {boolean} whether to disable release btn or not during document release
   */
  export let disableReleaseBtn
  /**
   * @type {string} 46 char, IPFS CID of the file
   */
  export let baseUri
  /**
   * @type {string} the text to be shown in the card
   */
  let cardText
  /**
   * @type {string} The card color (hex code) used for styling elements.
   */
  let cardColor
  /**
   * @type {string} The text color (hex code) used for styling elements.
   */
  let textColor
  /**
   * @type {boolean} whether to show the canvas image or not
   */
  let showImage = false
  /**
   * @type {boolean} whether to show loading screen or not
   */
  let showLoading = false

  let imageBlobUrl = ""
  /**
   * @typedef {Object} closeNotification
   * @property {Function} close - Closes the notification.
   */
  /**
   * @type {closeNotification}
   */
  let closeNotification = {
    close() {},
  }

  onMount(() => {
    changeColorAndText()
  })
  afterUpdate(() => {
    changeColorAndText()
  })

  /**
   * Function for changing the color and text of the card
   */
  const changeColorAndText = () => {
    // Check if the document is marked as failed
    if (documentData.isDocumentFailed) {
      // If the document failed, set cardColor and textColor accordingly
      cardColor = "#f8d7da" // Red background color
      textColor = "#721c24" // Dark text color
    } else {
      // If the document is not marked as failed
      if (!documentData.fileHash) {
        // Check if fileHash is not available
        if (documentData.generatedFile) {
          // If a generated file is available, set text and colors accordingly
          cardText = "Uploading Generated Document..." // Text for uploading
          cardColor = "#CCEAE8" // Light blue background color
          textColor = "#1F51FF" // Blue text color
          return // Exit the function
        } else {
          // If no generated file is available, set text and colors for waiting
          cardText = "Waiting for document generation..." // Text for waiting
          cardColor = "#F9E0C2" // Light orange background color
          textColor = "#EA9633" // Orange text color
          return // Exit the function
        }
      } else {
        // If fileHash is available, set text and colors accordingly
        cardColor = "#C2FFE9" // Light green background color
        textColor = "#005620" // Dark green text color
        return // Exit the function
      }
    }
  }

  /**
   * Function for releasig the document
   */
  const releaseDocument = async () => {
    try {
      // Set showLoading and disableReleaseBtn to true to indicate loading state
      showLoading = true
      disableReleaseBtn = true

      // Call the releaseSelectedDocument function with documentData.id
      await releaseSelectedDocument(documentData.id)

      // Get the receiver's name from documentData
      let receiver = documentData.recName

      let isFileGenerated = documentData.generatedFile

      // Remove the released document from the $bulkJson array
      $bulkJson = $bulkJson.filter((a) => a.id != documentData.id)

      // Display a success notification indicating the successful release
      notify.success(`Document of <strong>${receiver}</strong> released Successfully`)

      // Dispatch an event named "release"
      dispatch("release", isFileGenerated)
    } catch (error) {
      const info = "PAGE: File Generation (Bulk Upload)\nEVENT: While releasing document"
      closeNotification.close()
      closeNotification = notify.danger(error, info)
    } finally {
      // Set showLoading and disableReleaseBtn back to false to end the loading state
      showLoading = false
      disableReleaseBtn = false
    }
  }

  afterUpdate(() => {
    loadTooltip()
    window.addEventListener("resize", loadTooltip)
  })

  const handlePreviewLoading = async () => {
    try {
      showImage = true
      if (imageBlobUrl) return
      showLoading = true
      const { data } = await axios.get(`/ipfs/${documentData.filename}`, {
        responseType: "blob",
      })

      // console.log(data, "data")

      imageBlobUrl = URL.createObjectURL(data)
    } catch (error) {
      // console.error(error)
      showImage = false
      closeNotification.close()
      const info = "PAGE: File Generation (Bulk Upload)\nEVENT: While fetching document from IPFS for document preview"
      closeNotification = notify.danger(error, info)
    } finally {
      showLoading = false
    }
  }
</script>

{#if showImage}
  {#if showLoading}
    <Loader>
      <span class="flex">
        {@html "Loading Image&nbsp;&nbsp;"}
      </span>
    </Loader>
  {:else}
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <div
      class="fixed inset-0 z-50 flex h-full w-full cursor-pointer flex-col items-center justify-center overflow-y-auto overflow-x-hidden bg-black backdrop-blur-md"
      on:click|preventDefault|stopPropagation={() => {
        showImage = false
      }}
    >
      <div class="flex items-center justify-center overflow-hidden">
        <div class="relative">
          <img on:click|stopPropagation src={imageBlobUrl} class="relative flex max-h-[80vh] min-w-[300px] max-w-[390px] cursor-default items-center justify-center rounded-md mobile:p-0 sm:max-w-[700px]" alt="" />
        </div>
      </div>
      <div class="mb-6 mt-3 flex items-center justify-center text-lg font-semibold text-white mobile:text-xl">To close preview, click within the black area.</div>
    </div>
  {/if}
{/if}

<div style="background: {cardColor};color: {textColor}" class="relative flex w-full items-center gap-3 rounded-md py-3 pl-2 pr-4 text-base font-medium">
  {#if !documentData.isDocumentFailed}
    {#if !documentData.fileHash}
      <div class="relative flex h-12 w-12 items-center justify-center">
        <div style="border-color: {textColor};color:{textColor}" class="absolute flex h-10 w-10 animate-spin rounded-full border-t-[3px] bg-transparent" />
        <div>
          <i class="fa-solid fa-file" />
        </div>
      </div>

      <h1 class="text-sm font-bold mobile:text-base">{cardText}</h1>
    {:else}
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <div on:click={handlePreviewLoading} class="relative flex h-fit w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-md border-2 border-[#22C55E] bg-transparent text-white">
        <img src={baseUri} class="rounded-xl" alt="GeneratedFile" />
        <div class="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#22C55E] text-white">
          <i class="fa-solid fa-check" />
        </div>
      </div>
      <!-- <div class="flex w-[90%] items-center"> -->
      <div class="w-2/5 truncate">
        <h1 class="truncate text-sm font-bold md:text-base">{documentData.recName ? documentData.recName : "Loading ..."}</h1>
        <h1 class="text-xs font-medium md:text-sm">{new Date().toLocaleString()}</h1>
      </div>
      <div class="ml-auto flex gap-6 md:gap-3">
        <button type="button" on:click={handlePreviewLoading} class="flex items-center">
          <span>
            <i class="fa-solid fa-eye" />
          </span>
          <span name="tooltip" id="Preview Image" tooltip-position="top" class="hidden text-sm font-bold lg:block">&nbsp;&nbsp;Preview&nbsp;Image&nbsp;</span>
        </button>
      </div>
    {/if}
  {:else}
    {#if showLoading}
      <Loader>
        <span class="flex">
          {@html "Releasing Document&nbsp;&nbsp;"}
        </span>
      </Loader>
    {/if}

    <div class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-500 text-lg text-white">
      <i class="fa-solid fa-triangle-exclamation" />
    </div>

    <div class="flex w-2/5 items-center gap-3 text-base font-medium">
      <h1 class="truncate">
        Document of <strong class="text-[#491217]">{documentData.recName}</strong>
        failed
      </h1>
      <button data-tooltip={documentData.errorMsg} tooltip-position="top">
        <i class="fa-solid fa-circle-info fa-lg text-[#491217]" />
      </button>
    </div>

    <div class="ml-auto flex gap-6 md:gap-3">
      <button disabled={disableReleaseBtn} type="button" on:click={releaseDocument} class="flex items-center rounded-md border border-red-500 px-4 py-1 disabled:cursor-not-allowed">
        <span>
          <i class="fa-solid fa-trash-can" />
        </span>
        <span class="hidden text-sm font-bold mobile:block">&nbsp;&nbsp;Release&nbsp;Document&nbsp;</span>
      </button>
    </div>
  {/if}
</div>
